// src/components/admin/tabs/pointsCenter/PointsPolicyPane.jsx
// 积分中心 · 限额策略：策略列表 + 新建/编辑弹窗。
// 本版本超限行为固定「告警」（WARN），不提供阻断选项；策略接口为后端 P1，失败时优雅提示。
import React, { useCallback, useEffect, useState } from "react";
import { Check, Pencil, Plus, RefreshCw, ShieldAlert, X } from "lucide-react";
import { createPointPolicy, fetchPointPolicies, updatePointPolicy } from "../../../../services/adminService";
import { displayUserId } from "../../adminUtils";
import {
  ADMIN_ACCENT,
  EmptyBlock,
  ErrorNotice,
  FilterInput,
  IconAction,
  Panel,
  PrimaryButton,
  ProjectCombobox,
  SecondaryButton,
  Segmented,
  StatusBadge,
} from "../../adminUi.jsx";
import { formatPoints } from "./pointsShared.js";

const SCOPE_OPTIONS = [
  { key: "ALL", label: "全员" },
  { key: "USER", label: "个人" },
  { key: "PROJECT", label: "项目" },
  { key: "USER_PROJECT", label: "个人+项目" },
];

const PERIOD_OPTIONS = [
  { key: "DAY", label: "每日" },
  { key: "MONTH", label: "每月" },
];

const scopeLabel = (scope) => SCOPE_OPTIONS.find((s) => s.key === scope)?.label || scope || "-";
const periodLabel = (period) => PERIOD_OPTIONS.find((p) => p.key === period)?.label || period || "-";

const EMPTY_FORM = {
  id: null,
  scope: "ALL",
  userId: "",
  projectId: "",
  period: "DAY",
  maxPoints: "",
  note: "",
  enabled: true,
};

const PointsPolicyPane = ({ projectOptions = [] }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offline, setOffline] = useState(false); // 策略接口未上线
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchPointPolicies();
      const list = Array.isArray(data) ? data : (data?.items || data?.content || []);
      setPolicies(list);
      setOffline(false);
    } catch (e) {
      console.error(e);
      setOffline(true);
      setError(e?.message || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const projectNameOf = (id) => {
    if (id == null || id === "") return null;
    const found = (projectOptions || []).find((p) => String(p.id) === String(id));
    return found?.name || `项目 ${id}`;
  };

  const scopeText = (policy) => {
    const parts = [scopeLabel(policy.scope)];
    if ((policy.scope === "USER" || policy.scope === "USER_PROJECT") && policy.userId) {
      parts.push(displayUserId(policy.userId));
    }
    if ((policy.scope === "PROJECT" || policy.scope === "USER_PROJECT") && policy.projectId != null && policy.projectId !== "") {
      parts.push(projectNameOf(policy.projectId) || `项目 ${policy.projectId}`);
    }
    return parts.join(" · ");
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setMessage("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (policy) => {
    setForm({
      id: policy.id,
      scope: policy.scope || "ALL",
      userId: policy.userId || "",
      projectId: policy.projectId != null ? String(policy.projectId) : "",
      period: policy.period || "DAY",
      maxPoints: String(policy.maxPoints ?? ""),
      note: policy.note || "",
      enabled: policy.enabled !== false,
    });
    setMessage("");
    setError("");
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.maxPoints || Number(form.maxPoints) <= 0) {
      setError("上限积分必须大于 0");
      return;
    }
    if ((form.scope === "USER" || form.scope === "USER_PROJECT") && !form.userId.trim()) {
      setError("范围为「个人」时必须填写用户 ID");
      return;
    }
    if ((form.scope === "PROJECT" || form.scope === "USER_PROJECT") && !form.projectId) {
      setError("范围为「项目」时必须选择项目");
      return;
    }
    const body = {
      scope: form.scope,
      period: form.period,
      maxPoints: Number(form.maxPoints),
      onExceeded: "WARN", // 本版本固定只告警
      note: form.note,
      enabled: form.enabled,
    };
    if (form.scope === "USER" || form.scope === "USER_PROJECT") body.userId = form.userId.trim();
    if (form.scope === "PROJECT" || form.scope === "USER_PROJECT") body.projectId = form.projectId;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (form.id != null) {
        await updatePointPolicy(form.id, body);
      } else {
        await createPointPolicy(body);
      }
      setModalOpen(false);
      setMessage(form.id != null ? "策略已更新" : "策略已创建");
      await load();
    } catch (e) {
      console.error(e);
      setError(`保存失败，策略接口可能未上线：${e?.message || "未知错误"}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (policy) => {
    setError("");
    setMessage("");
    try {
      await updatePointPolicy(policy.id, { enabled: policy.enabled === false });
      setMessage(policy.enabled === false ? "策略已启用" : "策略已停用");
      await load();
    } catch (e) {
      console.error(e);
      setError(`操作失败，策略接口可能未上线：${e?.message || "未知错误"}`);
    }
  };

  const needsUser = form.scope === "USER" || form.scope === "USER_PROJECT";
  const needsProject = form.scope === "PROJECT" || form.scope === "USER_PROJECT";

  return (
    <div className="space-y-4">
      {offline && (
        <div
          className="rounded-lg px-3.5 py-2.5 text-sm flex flex-wrap items-center gap-3 animate-in fade-in duration-200"
          style={{ backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--color-warning) 35%, transparent)", color: "var(--color-warning)" }}
        >
          <ShieldAlert size={14} className="shrink-0" />
          <span className="flex-1 min-w-0">
            限额策略接口未上线（后端 P1 规划中）。页面已按接口契约就绪，上线后即可使用。{error ? `（${error}）` : ""}
          </span>
          <SecondaryButton onClick={load} disabled={loading}>重试</SecondaryButton>
        </div>
      )}
      {!offline && error && <ErrorNotice message={error} />}
      {message && (
        <div
          className="rounded-lg px-3.5 py-2.5 text-sm animate-in fade-in slide-in-from-top-1 duration-200"
          style={{ backgroundColor: "color-mix(in srgb, var(--color-success) 12%, transparent)", border: "1px solid var(--color-success)", color: "var(--color-success)" }}
        >
          {message}
        </div>
      )}

      <Panel
        title="限额策略"
        sub="按积分金额限制 · 本版本超限只告警（WARN），不阻断生成"
        right={(
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              title="刷新"
              className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
              style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
            <PrimaryButton onClick={openCreate} className="inline-flex items-center gap-1.5">
              <Plus size={14} />
              新建策略
            </PrimaryButton>
          </div>
        )}
      >
        {!policies.length ? (
          <EmptyBlock
            text={offline ? "策略接口未上线" : "暂无限额策略"}
            hint={offline ? "GET /api/admin/points/policies 尚未可用" : "新建一条策略开始监控积分消耗"}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-label text-left">
                  <th className="font-medium pb-2.5 pr-4">范围</th>
                  <th className="font-medium pb-2.5 pr-4">周期</th>
                  <th className="font-medium pb-2.5 pr-4 text-right">上限积分</th>
                  <th className="font-medium pb-2.5 pr-4">超限行为</th>
                  <th className="font-medium pb-2.5 pr-4">备注</th>
                  <th className="font-medium pb-2.5 pr-4">状态</th>
                  <th className="font-medium pb-2.5">操作</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((policy) => {
                  const enabled = policy.enabled !== false;
                  return (
                    <tr
                      key={policy.id}
                      className="hover:bg-[var(--color-bg-tertiary)] transition-colors"
                      style={{ borderTop: "1px solid var(--color-border-secondary)" }}
                    >
                      <td className="py-2.5 pr-4 text-xs font-medium max-w-56 truncate" title={scopeText(policy)}>{scopeText(policy)}</td>
                      <td className="py-2.5 pr-4 text-xs whitespace-nowrap">{periodLabel(policy.period)}</td>
                      <td className="py-2.5 pr-4 text-xs font-mono-num font-semibold text-right whitespace-nowrap" style={{ color: ADMIN_ACCENT }}>
                        {formatPoints(policy.maxPoints)}
                      </td>
                      <td className="py-2.5 pr-4"><StatusBadge label="告警" color="var(--color-warning)" /></td>
                      <td className="py-2.5 pr-4 text-xs max-w-48 truncate" title={policy.note || ""} style={{ color: "var(--color-text-secondary)" }}>
                        {policy.note || "-"}
                      </td>
                      <td className="py-2.5 pr-4">
                        <StatusBadge label={enabled ? "启用" : "停用"} color={enabled ? "var(--color-success)" : "var(--color-text-muted)"} />
                      </td>
                      <td className="py-2.5">
                        <div className="flex gap-1">
                          <IconAction title="编辑策略" onClick={() => openEdit(policy)} icon={Pencil} />
                          <IconAction title={enabled ? "停用策略" : "启用策略"} onClick={() => toggleEnabled(policy)} icon={enabled ? X : Check} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* 新建 / 编辑弹窗 */}
      {modalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 animate-in fade-in duration-150"
          style={{ backgroundColor: "rgba(0,0,0,0.65)", zIndex: "var(--z-modal)" }}
          onClick={() => setModalOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-xl border overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-secondary)", boxShadow: "var(--shadow-modal)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="h-14 px-5 flex items-center justify-between border-b" style={{ borderColor: "var(--color-border-secondary)" }}>
              <div className="text-sm font-semibold">{form.id != null ? "编辑限额策略" : "新建限额策略"}</div>
              <IconAction title="关闭" onClick={() => setModalOpen(false)} icon={X} />
            </header>
            <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto custom-scrollbar">
              {error && <ErrorNotice message={error} />}

              <div>
                <label className="text-label block mb-1.5">范围</label>
                <Segmented options={SCOPE_OPTIONS} value={form.scope} onChange={(v) => setForm((c) => ({ ...c, scope: v }))} ariaLabel="策略范围" />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {needsUser && (
                  <FilterInput label="用户 ID" value={form.userId} placeholder="用户名" onChange={(v) => setForm((c) => ({ ...c, userId: v }))} />
                )}
                {needsProject && (
                  <div>
                    <label className="text-label block mb-1.5">项目</label>
                    <ProjectCombobox
                      value={form.projectId}
                      onChange={(v) => setForm((c) => ({ ...c, projectId: v }))}
                      projectOptions={projectOptions}
                      placeholder="选择项目"
                      maxWidth={280}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="text-label block mb-1.5">周期</label>
                <Segmented options={PERIOD_OPTIONS} value={form.period} onChange={(v) => setForm((c) => ({ ...c, period: v }))} ariaLabel="统计周期" />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <FilterInput label="上限积分" type="number" min="1" step="1" value={form.maxPoints} onChange={(v) => setForm((c) => ({ ...c, maxPoints: v }))} />
                <FilterInput label="备注" value={form.note} onChange={(v) => setForm((c) => ({ ...c, note: v }))} />
              </div>

              <div className="flex items-center justify-between rounded-lg border px-3.5 py-2.5" style={{ borderColor: "var(--color-border-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>超限行为</span>
                <StatusBadge label="告警（本版本不支持阻断）" color="var(--color-warning)" />
              </div>

              <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
                超限后仅进入管理端告警列表，不打断成员生成、不触达成员端。
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <SecondaryButton onClick={() => setModalOpen(false)} disabled={saving}>取消</SecondaryButton>
                <PrimaryButton onClick={submit} disabled={saving}>
                  {saving ? "保存中…" : form.id != null ? "保存策略" : "创建策略"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsPolicyPane;
